import React, { useState, useEffect, useCallback } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Badge } from '../ui/badge';
import { catalogAPI, pricingAPI } from '../../utils/api';
import { Plus, Edit, Settings2, RefreshCw, Calculator, Clock, TrendingUp, ChevronDown, ChevronRight, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';

export default function RentalDefaultsPanel() {
  const [defaults, setDefaults] = useState([]);
  const [snapshots, setSnapshots] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [recomputing, setRecomputing] = useState(false);
  const [showSnapshots, setShowSnapshots] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [dRes, sRes, vRes] = await Promise.all([
        catalogAPI.getRentalDefaults(),
        catalogAPI.getRentalSnapshots(),
        pricingAPI.getVendors(),
      ]);
      setDefaults(dRes.data);
      setSnapshots(sRes.data);
      setVendors(vRes.data);
    } catch { toast.error('Failed to load rental defaults'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const getVendorName = (vid) => {
    if (!vid) return 'Global (All Vendors)';
    return vendors.find(v => v.vendor_id === vid)?.name || vid;
  };

  const handleRecomputeAll = async () => {
    if (!window.confirm('Recompute rental prices for all SKUs in the sheet? This will create new active snapshots.')) return;
    setRecomputing(true);
    try {
      // Get all sheet rows, then bulk recompute
      const sheetRes = await catalogAPI.getSheet();
      const allIds = sheetRes.data.map(r => r.sheet_row_id);
      if (allIds.length === 0) {
        toast.error('No SKU sheet rows found. Sync from Cost Matrix first.');
        setRecomputing(false);
        return;
      }
      const res = await catalogAPI.bulkSheetAction({ action: 'recompute_rent', row_ids: allIds });
      const data = res.data;
      toast.success(`Computed: ${data.computed}${data.errors?.length ? `, Errors: ${data.errors.length}` : ''}`);
      if (data.errors?.length) {
        data.errors.forEach(e => toast.error(`${e.row_id}: ${e.error}`, { duration: 6000 }));
      }
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Recompute failed'); }
    finally { setRecomputing(false); }
  };

  const fmt = (n) => n != null ? `₹${Number(n).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 4 })}` : '—';
  const pct = (n) => n != null ? `${(Number(n) * 100).toFixed(1)}%` : '—';

  if (loading) return <div className="flex items-center justify-center py-16"><div className="w-6 h-6 border-2 border-slate-300 border-t-slate-800 rounded-full animate-spin" /></div>;

  const activeDefaults = defaults.filter(d => d.status === 'ACTIVE');
  const activeSnapshots = snapshots.filter(s => s.status === 'ACTIVE');

  return (
    <div className="space-y-6">
      {/* -- DEFAULTS SECTION -- */}
      <div>
        <div className="flex justify-between items-center mb-4">
          <div>
            <h3 className="text-sm font-semibold text-slate-900 flex items-center gap-2">
              <Settings2 className="w-4 h-4" /> Rental Pricing Defaults
            </h3>
            <p className="text-xs text-slate-500 mt-0.5">Financial levers used to compute "starting at" rental prices</p>
          </div>
          <div className="flex items-center gap-2">
            <Button size="sm" variant="outline" className="h-8 text-xs" onClick={handleRecomputeAll} disabled={recomputing} data-testid="recompute-all-btn">
              <RefreshCw className={`w-3.5 h-3.5 mr-1 ${recomputing ? 'animate-spin' : ''}`} />
              {recomputing ? 'Computing...' : 'Recompute All'}
            </Button>
            <Button size="sm" className="bg-slate-900 h-8" onClick={() => { setEditItem(null); setShowForm(true); }} data-testid="add-rental-defaults-btn">
              <Plus className="w-4 h-4 mr-1" /> Add Defaults
            </Button>
          </div>
        </div>

        {defaults.length === 0 ? (
          <div className="text-center py-12 bg-white border border-slate-200 rounded-sm">
            <Calculator className="w-12 h-12 mx-auto mb-3 text-slate-300" />
            <p className="text-sm text-slate-500">No rental pricing defaults configured</p>
            <p className="text-xs text-slate-400 mt-1">Add defaults to enable the rental price engine</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {defaults.map(d => (
              <div key={d.defaults_id} className={`bg-white border rounded-sm shadow-sm p-4 ${d.status === 'ACTIVE' ? 'border-emerald-200' : 'border-slate-200 opacity-60'}`} data-testid={`defaults-card-${d.defaults_id}`}>
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <div className={`w-2 h-2 rounded-full ${d.status === 'ACTIVE' ? 'bg-emerald-500' : 'bg-slate-300'}`} />
                    <span className="text-sm font-medium text-slate-900">{d.name}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge className={d.status === 'ACTIVE' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-slate-100 text-slate-500'}>
                      {d.status}
                    </Badge>
                    <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => { setEditItem(d); setShowForm(true); }} data-testid={`edit-defaults-${d.defaults_id}`}>
                      <Edit className="w-3 h-3 mr-1" /> Edit
                    </Button>
                  </div>
                </div>
                <p className="text-xs text-slate-500 mb-3">{getVendorName(d.vendor_id)} &middot; {d.region}</p>
                <div className="grid grid-cols-3 gap-2 text-xs">
                  <LeverItem label="Target IRR" value={pct(d.target_irr_annual)} icon={TrendingUp} />
                  <LeverItem label="Term" value={`${d.contract_term_months}mo`} icon={Clock} />
                  <LeverItem label="Buffer" value={pct(d.negotiation_buffer_pct)} />
                  <LeverItem label="Commission" value={fmt(d.commissioning_charge)} />
                  <LeverItem label="SM Monthly" value={fmt(d.sm_charge_fixed_monthly)} />
                  <LeverItem label="Deposit" value={`${d.deposit_months}mo`} />
                  <LeverItem label="GST" value={pct(d.gst_rate)} />
                  <LeverItem label="Min Commit" value={pct(d.min_committed_pct)} />
                  <LeverItem label="Discharge" value={`${d.expected_discharge_kwh_per_month} kWh/mo`} />
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* -- SNAPSHOTS SECTION -- */}
      <div>
        <button
          className="flex items-center gap-2 text-sm font-semibold text-slate-900 mb-3 hover:text-slate-700 transition-colors"
          onClick={() => setShowSnapshots(!showSnapshots)}
          data-testid="toggle-snapshots-btn"
        >
          {showSnapshots ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
          <Calculator className="w-4 h-4" />
          Rental SKU Snapshots
          <span className="text-xs font-normal text-slate-500 ml-1">({activeSnapshots.length} active / {snapshots.length} total)</span>
        </button>

        {showSnapshots && (
          snapshots.length === 0 ? (
            <div className="text-center py-8 bg-white border border-slate-200 rounded-sm">
              <p className="text-sm text-slate-500">No rental snapshots computed yet</p>
              <p className="text-xs text-slate-400 mt-1">Click "Recompute All" after configuring defaults and cost maps</p>
            </div>
          ) : (
            <div className="bg-white border border-slate-200 rounded-sm shadow-sm overflow-x-auto">
              <table className="w-full text-sm" data-testid="snapshots-table">
                <thead>
                  <tr className="border-b border-slate-200 bg-slate-50">
                    <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase">SKU ID</th>
                    <th className="text-right px-4 py-3 text-xs font-semibold text-emerald-700 uppercase bg-emerald-50/50">Rent From (₹/kWh/mo)</th>
                    <th className="text-center px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Status</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Assumptions</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Computed</th>
                    <th className="text-center px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Flags</th>
                    <th className="text-right px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-50">
                  {snapshots.map(s => (
                    <SnapshotRow key={s.rental_snapshot_id} snapshot={s} onRecomputed={fetchData} />
                  ))}
                </tbody>
              </table>
            </div>
          )
        )}
      </div>

      {showForm && (
        <DefaultsForm
          item={editItem}
          vendors={vendors}
          onClose={() => setShowForm(false)}
          onSaved={() => { setShowForm(false); fetchData(); }}
        />
      )}
    </div>
  );
}

function SnapshotRow({ snapshot: s, onRecomputed }) {
  const [recomputing, setRecomputing] = useState(false);

  const handleRecompute = async () => {
    setRecomputing(true);
    try {
      await catalogAPI.recomputeRent(s.sku_id);
      toast.success(`Recomputed rental for ${s.sku_id}`);
      onRecomputed();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to recompute'); }
    finally { setRecomputing(false); }
  };

  return (
    <tr className={`hover:bg-slate-50 ${s.status !== 'ACTIVE' ? 'opacity-50' : ''}`} data-testid={`snapshot-row-${s.rental_snapshot_id}`}>
      <td className="px-4 py-3"><span className="font-mono text-xs font-medium text-slate-900">{s.sku_id}</span></td>
      <td className="px-4 py-3 text-right bg-emerald-50/30">
        <span className="font-mono text-sm font-bold text-emerald-700">₹{Number(s.rent_from_inr_per_kwh_month).toFixed(4)}</span>
      </td>
      <td className="px-4 py-3 text-center">
        <Badge className={s.status === 'ACTIVE' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-slate-100 text-slate-500'}>
          {s.status}
        </Badge>
      </td>
      <td className="px-4 py-3 text-xs text-slate-500 max-w-xs truncate">{s.assumptions_text || '—'}</td>
      <td className="px-4 py-3 text-xs text-slate-500">
        {s.computed_at ? new Date(s.computed_at).toLocaleString() : '—'}
        <br /><span className="text-[10px] text-slate-400">by {s.computed_by}</span>
      </td>
      <td className="px-4 py-3 text-center">
        {s.data_quality_flags?.length > 0 ? (
          <span className="inline-flex items-center text-[10px] text-amber-600" title={s.data_quality_flags.join(', ')}>
            <AlertTriangle className="w-3 h-3 mr-0.5" /> {s.data_quality_flags.length}
          </span>
        ) : <span className="text-emerald-500 text-[10px]">Clean</span>}
      </td>
      <td className="px-4 py-3 text-right">
        {s.status === 'ACTIVE' && (
          <Button size="sm" variant="outline" className="h-7 text-xs" onClick={handleRecompute} disabled={recomputing} data-testid={`recompute-${s.sku_id}`}>
            <RefreshCw className={`w-3 h-3 mr-1 ${recomputing ? 'animate-spin' : ''}`} /> Recompute
          </Button>
        )}
      </td>
    </tr>
  );
}

function LeverItem({ label, value, icon: Icon }) {
  return (
    <div className="bg-slate-50 rounded px-2 py-1.5">
      <p className="text-[10px] text-slate-400 uppercase flex items-center gap-1">
        {Icon && <Icon className="w-2.5 h-2.5" />} {label}
      </p>
      <p className="font-mono text-xs font-medium text-slate-800 mt-0.5">{value}</p>
    </div>
  );
}

/* --- Defaults Form --- */
function DefaultsForm({ item, vendors, onClose, onSaved }) {
  const [form, setForm] = useState({
    name: item?.name || 'Global Defaults',
    vendor_id: item?.vendor_id || '',
    region: item?.region || 'India',
    currency: item?.currency || 'INR',
    target_irr_annual: item?.target_irr_annual ?? 0.18,
    contract_term_months: item?.contract_term_months ?? 60,
    negotiation_buffer_pct: item?.negotiation_buffer_pct ?? 0.05,
    sm_charge_pct_of_rental: item?.sm_charge_pct_of_rental ?? 0,
    sm_charge_fixed_monthly: item?.sm_charge_fixed_monthly ?? 0,
    commissioning_charge: item?.commissioning_charge ?? 200000,
    decommissioning_charge: item?.decommissioning_charge ?? 0,
    deposit_months: item?.deposit_months ?? 3,
    gst_rate: item?.gst_rate ?? 0.18,
    expected_discharge_kwh_per_month: item?.expected_discharge_kwh_per_month ?? 14400,
    min_committed_pct: item?.min_committed_pct ?? 0.70,
    salvage_pct: item?.salvage_pct ?? 0,
    deposit_basis: item?.deposit_basis || 'energy_only',
  });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm(prev => ({ ...prev, [k]: v }));
  const setNum = (k, v) => set(k, v === '' ? '' : Number(v));

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = { ...form };
      // Convert all numeric strings
      Object.keys(payload).forEach(k => {
        if (typeof payload[k] === 'string' && payload[k] !== '' && !isNaN(payload[k]) && !['name', 'vendor_id', 'region', 'currency', 'deposit_basis'].includes(k)) {
          payload[k] = Number(payload[k]);
        }
      });
      if (item) {
        await catalogAPI.updateRentalDefaults(item.defaults_id, payload);
        toast.success('Rental defaults updated');
      } else {
        await catalogAPI.createRentalDefaults(payload);
        toast.success('Rental defaults created');
      }
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to save'); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader><DialogTitle>{item ? 'Edit' : 'Create'} Rental Pricing Defaults</DialogTitle></DialogHeader>
        <div className="space-y-4 mt-2">
          {/* Identity */}
          <div className="grid grid-cols-3 gap-3">
            <div>
              <Label className="text-xs">Name</Label>
              <Input value={form.name} onChange={e => set('name', e.target.value)} data-testid="defaults-name-input" />
            </div>
            <div>
              <Label className="text-xs">Vendor (empty = Global)</Label>
              <Select value={form.vendor_id || '__global__'} onValueChange={v => set('vendor_id', v === '__global__' ? '' : v)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__global__">Global (All Vendors)</SelectItem>
                  {vendors.map(v => <SelectItem key={v.vendor_id} value={v.vendor_id}>{v.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Region</Label>
              <Input value={form.region} onChange={e => set('region', e.target.value)} />
            </div>
          </div>

          {/* Financial Levers */}
          <div className="bg-slate-50 rounded-sm p-4 space-y-3">
            <p className="text-xs font-semibold text-slate-700 uppercase tracking-wider">Financial Levers</p>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label className="text-xs">Target IRR (Annual)</Label>
                <Input type="number" step="0.01" value={form.target_irr_annual} onChange={e => setNum('target_irr_annual', e.target.value)} data-testid="defaults-irr-input" />
                <p className="text-[10px] text-slate-400 mt-0.5">e.g. 0.18 = 18%</p>
              </div>
              <div>
                <Label className="text-xs">Contract Term (months)</Label>
                <Input type="number" value={form.contract_term_months} onChange={e => setNum('contract_term_months', e.target.value)} data-testid="defaults-term-input" />
              </div>
              <div>
                <Label className="text-xs">Negotiation Buffer</Label>
                <Input type="number" step="0.01" value={form.negotiation_buffer_pct} onChange={e => setNum('negotiation_buffer_pct', e.target.value)} />
                <p className="text-[10px] text-slate-400 mt-0.5">e.g. 0.05 = 5%</p>
              </div>
            </div>
          </div>

          {/* Operational Costs */}
          <div className="bg-slate-50 rounded-sm p-4 space-y-3">
            <p className="text-xs font-semibold text-slate-700 uppercase tracking-wider">Operational Costs</p>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label className="text-xs">Commissioning (INR)</Label>
                <Input type="number" value={form.commissioning_charge} onChange={e => setNum('commissioning_charge', e.target.value)} />
              </div>
              <div>
                <Label className="text-xs">Decommissioning (INR)</Label>
                <Input type="number" value={form.decommissioning_charge} onChange={e => setNum('decommissioning_charge', e.target.value)} />
              </div>
              <div>
                <Label className="text-xs">SM Fixed Monthly (INR)</Label>
                <Input type="number" value={form.sm_charge_fixed_monthly} onChange={e => setNum('sm_charge_fixed_monthly', e.target.value)} />
              </div>
            </div>
          </div>

          {/* Energy & Deposit */}
          <div className="bg-slate-50 rounded-sm p-4 space-y-3">
            <p className="text-xs font-semibold text-slate-700 uppercase tracking-wider">Energy & Deposit</p>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label className="text-xs">Expected Discharge (kWh/mo)</Label>
                <Input type="number" value={form.expected_discharge_kwh_per_month} onChange={e => setNum('expected_discharge_kwh_per_month', e.target.value)} />
              </div>
              <div>
                <Label className="text-xs">Min Committed %</Label>
                <Input type="number" step="0.01" value={form.min_committed_pct} onChange={e => setNum('min_committed_pct', e.target.value)} />
                <p className="text-[10px] text-slate-400 mt-0.5">e.g. 0.70 = 70%</p>
              </div>
              <div>
                <Label className="text-xs">Deposit Months</Label>
                <Input type="number" value={form.deposit_months} onChange={e => setNum('deposit_months', e.target.value)} />
              </div>
              <div>
                <Label className="text-xs">GST Rate</Label>
                <Input type="number" step="0.01" value={form.gst_rate} onChange={e => setNum('gst_rate', e.target.value)} />
              </div>
              <div>
                <Label className="text-xs">Salvage %</Label>
                <Input type="number" step="0.01" value={form.salvage_pct} onChange={e => setNum('salvage_pct', e.target.value)} />
              </div>
              <div>
                <Label className="text-xs">Deposit Basis</Label>
                <Select value={form.deposit_basis} onValueChange={v => set('deposit_basis', v)}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="energy_only">Energy Only</SelectItem>
                    <SelectItem value="energy_plus_sm">Energy + SM</SelectItem>
                    <SelectItem value="custom">Custom</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={onClose}>Cancel</Button>
            <Button className="bg-slate-900" onClick={handleSave} disabled={saving} data-testid="save-rental-defaults-btn">
              {saving ? 'Saving...' : item ? 'Update' : 'Create'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
